(() => {
  if (window.customTooltipEnhancementsInitialized) return;
  window.customTooltipEnhancementsInitialized = true;

  const TOOLTIP_SELECTOR = '[data-custom-tooltip]';
  const TOOLTIP_MIN_WIDTH = 80;
  const TOOLTIP_MAX_WIDTH = 480;
  const desktopMedia = window.matchMedia(
    '(hover: hover) and (pointer: fine)'
  );

  function getTooltipContent(reference) {
    const template = reference.querySelector('.tooltip-content');

    if (template) {
      const content = template.cloneNode(true);

      content.hidden = false;
      content.removeAttribute('hidden');
      content.classList.remove('hidden');

      return content;
    }

    return reference.dataset.tooltip || '';
  }

  function isTooltipOverflowing(reference) {
    if (!reference) return false;

    const horizontalOverflow =
      reference.scrollWidth > reference.clientWidth + 1;
    const verticalOverflow =
      reference.scrollHeight > reference.clientHeight + 1;

    if (horizontalOverflow || verticalOverflow) return true;

    const clone = reference.cloneNode(true);

    Object.assign(clone.style, {
      position: 'fixed',
      left: '-99999px',
      top: '0',
      width: `${reference.clientWidth}px`,
      height: 'auto',
      maxHeight: 'none',
      overflow: 'visible',
      display: 'block',
      WebkitLineClamp: 'unset',
      WebkitBoxOrient: 'unset',
      visibility: 'hidden',
      pointerEvents: 'none'
    });

    document.body.appendChild(clone);

    const fullHeight = clone.getBoundingClientRect().height;
    const visibleHeight = reference.getBoundingClientRect().height;

    clone.remove();

    return fullHeight > visibleHeight + 1;
  }

  function getTooltipMaxWidth(reference) {
    const referenceWidth = Math.floor(
      reference.getBoundingClientRect().width
    );

    return Math.min(
      Math.max(referenceWidth, TOOLTIP_MIN_WIDTH),
      TOOLTIP_MAX_WIDTH
    );
  }

  function getRightReferenceRect(reference) {
    const referenceRect = reference.getBoundingClientRect();
    const x = referenceRect.right;

    return {
      width: 0,
      height: referenceRect.height,
      top: referenceRect.top,
      bottom: referenceRect.bottom,
      left: x,
      right: x,
      x,
      y: referenceRect.top,
      toJSON() {
        return this;
      }
    };
  }

  function updateTooltip(instance) {
    const reference = instance.reference;

    instance.setProps({
      maxWidth: getTooltipMaxWidth(reference)
    });

    if (
      instance.state.isVisible &&
      !isTooltipOverflowing(reference)
    ) {
      instance.hide();
      return;
    }

    if (instance.popperInstance) {
      instance.popperInstance.update();
    }
  }

  function preventTooltipLinkNavigation(event) {
    if (!desktopMedia.matches) {
      event.preventDefault();
    }
  }

  function getTooltipElements(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return [];

    const elements = Array.from(
      root.querySelectorAll(TOOLTIP_SELECTOR)
    );

    if (
      root instanceof Element &&
      root.matches(TOOLTIP_SELECTOR)
    ) {
      elements.unshift(root);
    }

    return elements;
  }

  function initTooltips(root = document) {
    if (typeof tippy !== 'function') return;

    const isDesktop = desktopMedia.matches;

    getTooltipElements(root).forEach((element) => {
      if (element._tippy) {
        element._tippy.destroy();
      }

      if (!element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '0');
      }

      if (element.hasAttribute('data-tooltip-prevent-link')) {
        element.addEventListener(
          'click',
          preventTooltipLinkNavigation
        );
      }

      tippy(element, {
        content: getTooltipContent,
        theme: 'custom-tooltip',
        trigger: isDesktop
          ? 'mouseenter focus'
          : 'click',
        placement: isDesktop
          ? 'top-end'
          : 'bottom-end',
        getReferenceClientRect() {
          return getRightReferenceRect(element);
        },
        maxWidth: getTooltipMaxWidth(element),
        offset: [0, 8],
        arrow: true,
        hideOnClick: !isDesktop,
        interactive: true,
        appendTo: () => document.body,
        delay: isDesktop
          ? [100, 80]
          : [0, 0],
        duration: [150, 100],
        touch: true,
        popperOptions: {
          modifiers: [
            {
              name: 'flip',
              options: {
                fallbackPlacements: isDesktop
                  ? ['bottom-end']
                  : ['top-end']
              }
            },
            {
              name: 'preventOverflow',
              options: {
                padding: 8
              }
            },
            {
              name: 'arrow',
              options: {
                padding: 8
              }
            }
          ]
        },
        onShow(instance) {
          const reference = instance.reference;

          if (!isTooltipOverflowing(reference)) {
            return false;
          }

          const content = getTooltipContent(reference);

          if (
            typeof content === 'string' &&
            !content.trim()
          ) {
            return false;
          }

          instance.setContent(content);
          updateTooltip(instance);
        },
        onMount(instance) {
          updateTooltip(instance);
        }
      });
    });
  }

  window.initTooltips = initTooltips;

  function initializeTooltips() {
    initTooltips();

    let resizeTimer;

    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimer);

        resizeTimer = setTimeout(() => {
          getTooltipElements(document).forEach((element) => {
            if (element._tippy) {
              updateTooltip(element._tippy);
            }
          });
        }, 80);
      },
      { passive: true }
    );

    const handleDeviceChange = () => initTooltips();

    if (desktopMedia.addEventListener) {
      desktopMedia.addEventListener(
        'change',
        handleDeviceChange
      );
    } else {
      desktopMedia.addListener(handleDeviceChange);
    }

    document.addEventListener('shopify:section:load', (event) => {
      initTooltips(event.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeTooltips,
      { once: true }
    );
  } else {
    initializeTooltips();
  }
})();
